import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

// Hero slider images from news articles
const heroSliderImages = [
  'https://customer-assets.emergentagent.com/job_cyberyouth/artifacts/yeh5v9aw_1-1.jpg',
  'https://customer-assets.emergentagent.com/job_secyouth/artifacts/anpbygnx_2-1.webp',
  'https://customer-assets.emergentagent.com/job_cyberyouth/artifacts/n7nczbmc_3-1.webp',
  'https://customer-assets.emergentagent.com/job_secyouth/artifacts/s2c8bn56_4-1.jpeg',
  'https://customer-assets.emergentagent.com/job_secyouth/artifacts/tnndjpuj_2-2.jpeg',
  'https://customer-assets.emergentagent.com/job_secyouth/artifacts/ouil767e_3-2.webp',
];

const HeroSection = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Auto-rotate images every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === heroSliderImages.length - 1 ? 0 : prevIndex + 1
      );
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const scrollToSection = (id) => {
    const element = document.querySelector(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleNavigate = (path) => {
    navigate(path);
    window.scrollTo(0, 0);
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      {/* Background Layers */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#060b1a] via-[#0a1628] to-[#0f1f33]" />
      
      {/* Animated Grid Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(rgba(77, 214, 255, 0.15) 1px, transparent 1px),
              linear-gradient(90deg, rgba(77, 214, 255, 0.15) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      {/* Glowing Orbs - Positioned for split layout */}
      <div className="absolute top-1/4 left-1/6 w-72 h-72 bg-[#4DFF88]/15 rounded-full blur-[120px] animate-pulse" />
      <div className="absolute bottom-1/3 left-1/4 w-64 h-64 bg-[#4DD6FF]/20 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: '1s' }} />
      <div className="absolute top-1/3 right-1/4 w-80 h-80 bg-[#9F7AEA]/15 rounded-full blur-[130px] animate-pulse" style={{ animationDelay: '2s' }} />
      <div className="absolute bottom-1/4 right-1/3 w-56 h-56 bg-[#4DD6FF]/10 rounded-full blur-[80px] animate-pulse" style={{ animationDelay: '0.5s' }} />

      {/* Main Content - Split Screen Layout */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          
          {/* Left Side - Text Content */}
          <div className="text-left">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#4DFF88]/30 bg-[#4DFF88]/5 mb-8">
              <span className="w-2 h-2 rounded-full bg-[#4DFF88] animate-pulse" />
              <span className="text-[#4DFF88] text-sm font-medium">{t('hero.badge')}</span>
            </div>

            {/* Title */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-white leading-tight mb-6">
              <span className="block">{t('hero.title1')}</span>
              <span className="block bg-gradient-to-r from-[#4DFF88] via-[#4DD6FF] to-[#9F7AEA] bg-clip-text text-transparent">
                {t('hero.title2')}
              </span>
            </h1>

            {/* Subtitle */}
            <p className="text-lg sm:text-xl text-gray-400 max-w-xl mb-10 leading-relaxed">
              {t('hero.subtitle')}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-start gap-4">
              <Button
                onClick={() => handleNavigate('/hakkimizda')}
                className="w-full sm:w-auto bg-[#4DFF88] hover:bg-[#4DFF88]/10 hover:text-[#4DFF88] text-[#060b1a] font-medium px-8 py-4 text-base rounded-none transition-all duration-300 flex items-center justify-center gap-2 group"
              >
                {t('hero.aboutBtn')}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                onClick={() => handleNavigate('/amac')}
                variant="outline"
                className="w-full sm:w-auto bg-white/5 hover:bg-white hover:text-[#060b1a] text-white border-none font-medium px-8 py-4 text-base rounded-none transition-all duration-300"
              >
                {t('hero.purposeBtn')}
              </Button>
            </div>
          </div>

          {/* Right Side - Hero Image Slider */}
          <div className="relative lg:pl-8">
            {/* Decorative Frame */}
            <div className="relative">
              {/* Glowing border effect */}
              <div className="absolute -inset-1 bg-gradient-to-r from-[#4DFF88]/40 via-[#4DD6FF]/30 to-[#9F7AEA]/40 rounded-2xl blur-sm opacity-70" />
              
              {/* Main Image Container with Slider */}
              <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-[#0a1628]/50 backdrop-blur-sm">
                {/* Image Slider */}
                <div className="relative aspect-[4/3] overflow-hidden">
                  {heroSliderImages.map((image, index) => (
                    <img
                      key={index}
                      src={image}
                      alt={`Dernek etkinliği ${index + 1}`}
                      className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out ${
                        index === currentImageIndex ? 'opacity-100' : 'opacity-0'
                      }`}
                    />
                  ))}
                </div>
                
                {/* Overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#060b1a]/80 via-transparent to-[#060b1a]/20 pointer-events-none" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#060b1a]/60 via-transparent to-transparent pointer-events-none" />
                
                {/* Cyber lines decoration */}
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#4DFF88] via-[#4DD6FF] to-transparent" />
                <div className="absolute bottom-0 right-0 w-full h-1 bg-gradient-to-l from-[#9F7AEA] via-[#4DD6FF] to-transparent" />
                <div className="absolute top-0 left-0 h-full w-1 bg-gradient-to-b from-[#4DFF88] via-transparent to-transparent" />
                <div className="absolute top-0 right-0 h-full w-1 bg-gradient-to-b from-transparent via-[#4DD6FF] to-[#9F7AEA]" />

                {/* Slider Indicators */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                  {heroSliderImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${
                        index === currentImageIndex 
                          ? 'bg-[#4DFF88] w-6' 
                          : 'bg-white/40 hover:bg-white/60'
                      }`}
                      aria-label={`Görsel ${index + 1}`}
                    />
                  ))}
                </div>
              </div>

              {/* Floating Tech Elements */}
              <div className="absolute -top-4 -right-4 w-16 h-16 rounded-lg bg-[#4DFF88]/10 border border-[#4DFF88]/30 backdrop-blur-sm flex items-center justify-center animate-bounce" style={{ animationDuration: '3s' }}>
                <div className="w-8 h-8 rounded bg-gradient-to-br from-[#4DFF88] to-[#4DD6FF] opacity-60" />
              </div>
              
              <div className="absolute -bottom-4 -left-4 w-20 h-20 rounded-xl bg-[#4DD6FF]/10 border border-[#4DD6FF]/30 backdrop-blur-sm flex items-center justify-center animate-pulse" style={{ animationDuration: '2.5s' }}>
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#4DD6FF] to-[#9F7AEA] opacity-60" />
              </div>

              {/* Data flow lines */}
              <svg className="absolute -right-8 top-1/2 -translate-y-1/2 w-16 h-40 opacity-30" viewBox="0 0 60 160">
                <path d="M30 0 L30 40 L50 60 L50 100 L30 120 L30 160" fill="none" stroke="url(#cyberGradient)" strokeWidth="2" strokeDasharray="8 4" />
                <defs>
                  <linearGradient id="cyberGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#4DFF88" />
                    <stop offset="50%" stopColor="#4DD6FF" />
                    <stop offset="100%" stopColor="#9F7AEA" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <button
          onClick={() => scrollToSection('#stats')}
          className="text-gray-500 hover:text-[#4DFF88] transition-colors"
        >
          <ChevronDown className="w-8 h-8" />
        </button>
      </div>
    </section>
  );
};

export default HeroSection;
